import BaseScene from "@Scenes/BaseScene";

/*-------------- Config --------------------*/
import * as Scenes from '@Config/scenes';
import Delay from '@Utils/Time/Delay';
import Game from '@Game';
import Config from '@Config';
import Devices from '@Config/devices';
import LogError from '@Utils/Logging/LogError';

/**
 * Controls Scene
 * Handles creation of controls gui
 */
class ControlsTutorial extends BaseScene
{
    /**
     * Scene elements
     */
    private leanBackSprite: Phaser.GameObjects.Sprite;
    private leanForwardSprite: Phaser.GameObjects.Sprite;
    private rightTouchIndicator: Phaser.GameObjects.Graphics;
    private leftTouchIndicator: Phaser.GameObjects.Graphics;
    private keyboardSprite: Phaser.GameObjects.Image;

    /**
     * Contructor
     * @param name - key name of the scene
     */
    constructor()
    {
        super(Scenes.CONTROLS_TUTORIAL);
    }

    /**
     * Enables input
     */
    protected async create()
    {
        if (await this.playerShouldSkipControls())
        {
            this.scene.start(Scenes.MAIN)
        }
        else
        {
            this.cameras.main.setBackgroundColor('#FFFFFF');

            this.createTitle();
            this.createText();

            this.createLeanBackAnimation();
            this.createLeanForwardAnimation();

            if( Config.DEVICE === Devices.MOBILE)
            {
                this.createControlButtons();
                this.createLeftTouchIndicator();
                this.createRightTouchIndicator();
            }
            else
            {
                this.createKeyboard();
            }

            this.startAnimations();
        }
    }

    /**
     * Creates keyboard sprite
     */
    private createKeyboard()
    {
        const gameSize = this.getGameSize();
        this.keyboardSprite = this.add.image(gameSize.width / 2, gameSize.height - 100, 'keyboard_controls', 'keyboard');
        this.keyboardSprite.setScale(0.75);
    }

    /**
     * Player should skip controls
     */
    private async playerShouldSkipControls()
    {
        let skipControls = false;

        try
        {
            const bestDistance = Game.lifetimeScore.getDistanceScore();

            if (bestDistance > 60)
            {
                skipControls = true;
            }
        }
        catch (error)
        {
            LogError(error);
        }

        return skipControls;
    }

    /**
     * Creates title
     */
    private createTitle()
    {
        const gameSize = this.getGameSize();
        const fontStyle = { fontFamily: 'Permanent Marker', color: '#000000', align: 'center', fontSize: 30 };

        const title = this.add.text(gameSize.width / 2, 70, 'Tutorial', fontStyle);
        title.setOrigin(0.5);

        const accelerateText = this.add.text(gameSize.width / 2, 120, 'Bike will automatically accelerate', fontStyle);
        accelerateText.setOrigin(0.5);
        accelerateText.setFontSize(25);
        accelerateText.setColor('#128EE2');
    }

    /**
     * Create text
     */
    private createText()
    {
        const gameSize = this.getGameSize();
        const fontStyle = { fontFamily: 'Permanent Marker', color: '#FFFFFF', align: 'center', fontSize: 30 };

        /*--------------- Lean Back --------------*/

        let leanBackLang = 'Touch To\nLean Back';

        if( Config.DEVICE === Devices.DESKTOP)
        {
            leanBackLang = 'Left Key\nTo Lean Back';
        }

        const leanBackText = this.add.text(170, gameSize.height / 2, leanBackLang, fontStyle);
        leanBackText.setStroke('#000000', 5);
        leanBackText.setOrigin(0.5);

        /*--------------- Lean Forward --------------*/

        let leanForwardLang = 'Touch To\nLean Forward';

        if (Config.DEVICE === Devices.DESKTOP)
        {
            leanForwardLang = 'Right Key\nTo Lean Forward';
        }

        const leanForwardText = this.add.text(gameSize.width - 170, gameSize.height / 2, leanForwardLang, fontStyle);
        leanForwardText.setStroke('#000000', 5);
        leanForwardText.setOrigin(0.5);
    }

    /**
     * Creates lean back animation
     */
    private createLeanBackAnimation()
    {
        const gameSize = this.getGameSize();

        const leanBackFrameNames = this.anims.generateFrameNames('tutorial_lean_back', { prefix: 'lean_back_', start: 0, end: 26, zeroPad: 0 });
        this.anims.create({ key: 'lean_back', frames: leanBackFrameNames, repeat: 2 });

        const config = {
            key: 'tutorial_lean_back',
            x: gameSize.width / 2,
            y: gameSize.height / 2,
            anims: 'lean_back'
        };

        const sprite = this.make.sprite(config);
        sprite.anims.stop();
        sprite.setVisible(false);

        this.add.existing(sprite);

        this.leanBackSprite = sprite;
    }

    /**
     * Creates lean back animation
     */
    private createLeanForwardAnimation()
    {
        const gameSize = this.getGameSize();

        const leanForwardFrameNames = this.anims.generateFrameNames('tutorial_lean_forward', { prefix: 'lean_forward_', start: 0, end: 26, zeroPad: 0 });
        this.anims.create({ key: 'lean_forward', frames: leanForwardFrameNames, repeat: 2 });

        const config = {
            key: 'tutorial_lean_forward',
            x: gameSize.width / 2,
            y: gameSize.height / 2,
            anims: 'lean_forward'
        };

        const sprite = this.make.sprite(config);
        sprite.anims.stop();
        sprite.setVisible(false);

        this.add.existing(sprite);

        this.leanForwardSprite = sprite;
    }

    /**
     * Creates control button
     */
    private createControlButtons()
    {
        const gameSize = this.getGameSize();

        const leftArrow = this.add.image(200, gameSize.height - 100, 'left_arrow');
        const rightArrow = this.add.image(gameSize.width - 200, gameSize.height - 100, 'right_arrow');

        leftArrow.setScale(0.5);
        rightArrow.setScale(0.5);
        leftArrow.alpha = 0.2;
        rightArrow.alpha = 0.2;
    }

    /**
     * Creates Left Touch Indicator
     */
    private createLeftTouchIndicator()
    {
        const gameSize = this.getGameSize();
        const graphics = this.add.graphics();
        graphics.fillStyle(0x128EE2, 0.4)
        graphics.fillCircle(0, 0, 300);
        graphics.fillCircle(0, 0, 200);
        graphics.x = 100;
        graphics.y = gameSize.height / 2
        graphics.setVisible(false);

        this.leftTouchIndicator = graphics;
    }

    /**
     * Creates right touch indicator
     */
    private createRightTouchIndicator()
    {
        const gameSize = this.getGameSize();
        const graphics = this.add.graphics();
        graphics.fillStyle(0x128EE2, 0.4)
        graphics.fillCircle(0, 0, 300);
        graphics.fillCircle(0, 0, 200);
        graphics.x = gameSize.width - 100;
        graphics.y = gameSize.height / 2;
        graphics.setVisible(false);

        this.rightTouchIndicator = graphics;
    }

    /**
     * Left touch indicator
     */
    private animateLeftTouchIndicator()
    {
        this.leftTouchIndicator.setVisible(true);
        this.add.tween({ targets: this.leftTouchIndicator, scaleX: 1.2, scaleY: 1.2, duration: 1000, alpha: 0, repeat: 2 });
    }

    /**
     * Right touch indicator
     */
    private animateRightTouchIndicator()
    {
        this.rightTouchIndicator.setVisible(true);
        this.add.tween({ targets: this.rightTouchIndicator, scaleX: 1.2, scaleY: 1.2, duration: 1000, alpha: 0, repeat: 2 });
    }

    /**
     * Animates right keyboard key
     */
    private async animateKeyboardKey(key)
    {
        this.keyboardSprite.setTexture('keyboard_controls', `keyboard_${key}`);

        await Delay(500);

        this.keyboardSprite.setTexture('keyboard_controls', 'keyboard');

        await Delay(500);

        this.keyboardSprite.setTexture('keyboard_controls', `keyboard_${key}`);

        await Delay(500);

        this.keyboardSprite.setTexture('keyboard_controls', 'keyboard');

        await Delay(500);

        this.keyboardSprite.setTexture('keyboard_controls', `keyboard_${key}`);

        await Delay(500);

        this.keyboardSprite.setTexture('keyboard_controls', 'keyboard');
    }

    /**
     * Starts animations
     */
    private async startAnimations()
    {
        this.leanBackSprite.setVisible(true);

        await Delay(500);

        if( Config.DEVICE === Devices.MOBILE)
        {
            this.animateLeftTouchIndicator();
        }
        else
        {
            this.animateKeyboardKey('left');
        }

        this.leanBackSprite.anims.play('lean_back');

        await Delay(3000);

        this.leanBackSprite.setVisible(false);

        if (Config.DEVICE === Devices.MOBILE)
        {
            this.animateRightTouchIndicator();
        }
        else
        {
            this.animateKeyboardKey('right');
        }

        this.leanForwardSprite.setVisible(true);
        this.leanForwardSprite.anims.play('lean_forward');

        await Delay(3000);

        this.cameras.main.fadeOut(1000, 255, 255, 255);

        await Delay(1000);

        this.scene.start(Scenes.MAIN)
    }
}

export default ControlsTutorial;